/**
 * Finance API Client
 *
 * Provides API methods for finance operations.
 * Balance data is ALWAYS calculated from transactions on the backend.
 *
 * Related: docs/engineering/CHARGE_SYSTEM.md
 */

import type {
  Transaction,
  Payment,
  Statement,
  FinanceStats,
  FinanceFilter,
  PaymentFilter,
  PaymentOrigin,
  BalanceSummary,
  BalanceCheckResult,
  TransactionsResponse,
  PaymentsResponse,
  CreatePaymentPayload,
  Invoice,
  InvoiceFilter,
  InvoicesResponse,
  InvoiceSortField,
  SapBalances,
} from '../../domain/types'
import { getHttpClient } from '@spcc/core'

// ============================================================
// BALANCE API (Real backend integration)
// ============================================================

/**
 * Balance API endpoints - calls real Strapi backend
 */
export const balanceApi = {
  /**
   * Get current customer's balance summary.
   * Balance is CALCULATED from transactions, never stored.
   */
  async getBalance(): Promise<BalanceSummary> {
    const http = getHttpClient()
    const response = await http.get<{ data: BalanceSummary }>('/api/customer-account/balance')
    return response.data.data
  },

  /**
   * Get balance for a specific customer account (admin only).
   */
  async getBalanceByAccount(customerAccountId: number): Promise<BalanceSummary> {
    const http = getHttpClient()
    const response = await http.get<{ data: BalanceSummary }>(`/api/customer-account/balance/${customerAccountId}`)
    return response.data.data
  },

  /**
   * Check if customer has sufficient balance for an amount.
   */
  async checkBalance(amount: number): Promise<BalanceCheckResult> {
    const http = getHttpClient()
    const response = await http.post<{ data: BalanceCheckResult }>('/api/customer-account/balance/check', { amount })
    return response.data.data
  },

  async getSapBalances(): Promise<SapBalances> {
    const http = getHttpClient()
    const response = await http.get<{ data: SapBalances }>('/api/customer-account/sap-balances')
    return response.data.data
  },

  async getSapBalancesCached(): Promise<SapBalances> {
    const http = getHttpClient()
    const response = await http.get<{ data: SapBalances }>('/api/customer-account/sap-balances?source=cache')
    return response.data.data
  },
}

// ============================================================
// TRANSACTION & FINANCE API (Real backend integration)
// ============================================================

/**
 * API response types from Strapi backend
 */
interface TransactionListResponse {
  data: Transaction[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

interface StatementResponse {
  data: Statement
}

interface StatsResponse {
  data: FinanceStats
}

interface PaymentListResponse {
  data: Array<{
    id: number
    attributes: Record<string, unknown>
  }>
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

interface TransactionQueryOptions {
  filter?: FinanceFilter
  page: number
  pageSize: number
  orderId?: string
  salesOrderId?: string
}

function buildTransactionListParams({ filter, page, pageSize, orderId, salesOrderId }: TransactionQueryOptions) {
  const params = new URLSearchParams()

  params.append('page', String(page))
  params.append('pageSize', String(pageSize))

  if (filter?.type && filter.type !== 'all') {
    params.append('type', filter.type)
  }
  if (filter?.status && filter.status !== 'all') {
    params.append('status', filter.status)
  }
  if (filter?.dateRange?.from) {
    params.append('from', filter.dateRange.from)
  }
  if (filter?.dateRange?.to) {
    params.append('to', filter.dateRange.to)
  }
  if (filter?.search) {
    params.append('search', filter.search)
  }
  if (orderId) {
    params.append('orderId', orderId)
  }
  if (salesOrderId) {
    params.append('salesOrderId', salesOrderId)
  }

  return params
}

/**
 * Finance API endpoints - calls real Strapi backend
 */
export const financeApi = {
  /**
   * Get paginated list of transactions with filtering
   */
  async getTransactions(
    filter?: FinanceFilter,
    page = 1,
    pageSize = 25,
    options?: { orderId?: string; salesOrderId?: string }
  ): Promise<TransactionsResponse> {
    const http = getHttpClient()
    const params = buildTransactionListParams({
      filter,
      page,
      pageSize,
      orderId: options?.orderId,
      salesOrderId: options?.salesOrderId,
    })

    const response = await http.get<TransactionListResponse>(`/api/transactions/list?${params.toString()}`)
    return {
      transactions: response.data.data,
      meta: response.data.meta.pagination,
    }
  },

  /**
   * Get transactions for a specific order
   */
  async getTransactionsByOrderId(orderId: string): Promise<TransactionsResponse> {
    return financeApi.getTransactions(undefined, 1, 100, { orderId })
  },

  /**
   * Get transactions for all orders under a sales order
   */
  async getTransactionsBySalesOrderId(salesOrderId: string): Promise<TransactionsResponse> {
    return financeApi.getTransactions(undefined, 1, 100, { salesOrderId })
  },

  /**
   * Get paginated payments list with filtering and sorting
   */
  async getPayments(
    filter?: PaymentFilter,
    page = 1,
    pageSize = 25,
    sort = 'paymentDate:desc'
  ): Promise<PaymentsResponse> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    params.append('pagination[page]', String(page))
    params.append('pagination[pageSize]', String(pageSize))
    params.append('sort', sort)

    if (filter?.status && filter.status !== 'all') {
      params.append('filters[paymentStatus][$eq]', filter.status)
    }
    if (filter?.method && filter.method !== 'all') {
      params.append('filters[paymentMethod][$eq]', filter.method)
    }
    if (filter?.dateRange?.from) {
      params.append('filters[sapPostingDate][$gte]', filter.dateRange.from)
    }
    if (filter?.dateRange?.to) {
      params.append('filters[sapPostingDate][$lte]', filter.dateRange.to)
    }
    if (filter?.search) {
      params.append('filters[$or][0][paymentNumber][$containsi]', filter.search)
      params.append('filters[$or][1][sapPaymentDocument][$containsi]', filter.search)
      params.append('filters[$or][2][reference][$containsi]', filter.search)
    }

    const response = await http.get<PaymentListResponse>(`/api/payments?${params.toString()}`)
    const rawData = Array.isArray(response.data.data) ? response.data.data : []
    const payments: Payment[] = rawData.map((item: any) => {
      const attrs = item.attributes || item
      const id = String(item.id || attrs.id)
      return {
        id,
        paymentNumber: attrs.paymentNumber || '',
        amount: { amount: Number(attrs.amount) || 0, currency: attrs.currency || 'SAR' },
        status: attrs.paymentStatus || 'pending',
        method: attrs.paymentMethod || 'bank_transfer',
        origin: (attrs.origin as PaymentOrigin) || 'portal',
        date: attrs.paymentDate || attrs.sapPostingDate || '',
        invoiceIds: [],
        reference: attrs.reference || undefined,
        notes: attrs.notes || undefined,
        sapPaymentDocument: attrs.sapPaymentDocument || undefined,
        createdAt: attrs.createdAt || '',
      }
    })

    return {
      payments,
      meta: response.data.meta?.pagination ?? { page: 1, pageSize: 25, pageCount: 0, total: 0 },
    }
  },

  /**
   * Export all payments for current tenant with optional filters.
   * Uses a dedicated backend endpoint that queries the DB directly.
   */
  async exportPayments(filter?: PaymentFilter): Promise<Payment[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.method && filter.method !== 'all') {
      params.append('method', filter.method)
    }
    if (filter?.dateRange?.from) {
      params.append('dateFrom', filter.dateRange.from)
    }
    if (filter?.dateRange?.to) {
      params.append('dateTo', filter.dateRange.to)
    }
    if (filter?.search) {
      params.append('search', filter.search)
    }

    const qs = params.toString()
    const response = await http.get<{ data: any[] }>(`/api/payments/export${qs ? `?${qs}` : ''}`)
    const rawData = Array.isArray(response.data.data) ? response.data.data : []

    return rawData.map((attrs: any) => ({
      id: String(attrs.id),
      paymentNumber: attrs.paymentNumber || '',
      amount: { amount: Number(attrs.amount) || 0, currency: attrs.currency || 'SAR' },
      status: attrs.paymentStatus || 'pending',
      method: attrs.paymentMethod || 'bank_transfer',
      origin: (attrs.origin as PaymentOrigin) || 'portal',
      date: attrs.sapPostingDate || attrs.paymentDate || '',
      invoiceIds: [],
      reference: attrs.reference || undefined,
      notes: attrs.notes || undefined,
      sapPaymentDocument: attrs.sapPaymentDocument || undefined,
      createdAt: attrs.createdAt || '',
    }))
  },

  /**
   * Create a new payment record.
   * Tenant is auto-injected by the tenant isolation middleware.
   * The backend afterCreate lifecycle triggers outbound SAP sync.
   */
  async createPayment(payload: CreatePaymentPayload): Promise<Payment> {
    const http = getHttpClient()
    const response = await http.post<{ data: any }>('/api/payments', {
      data: {
        amount: payload.amount,
        currency: payload.currency || 'SAR',
        paymentMethod: payload.paymentMethod,
        paymentDate: payload.paymentDate,
        paymentStatus: 'pending',
      },
    })
    const item = response.data.data
    const attrs = item?.attributes || item
    return {
      id: String(item.id || attrs.id),
      paymentNumber: attrs.paymentNumber || '',
      amount: { amount: Number(attrs.amount) || payload.amount, currency: attrs.currency || payload.currency || 'SAR' },
      status: attrs.paymentStatus || 'pending',
      method: attrs.paymentMethod || payload.paymentMethod,
      origin: (attrs.origin as PaymentOrigin) || 'portal',
      date: attrs.paymentDate || payload.paymentDate,
      invoiceIds: [],
      reference: attrs.reference || undefined,
      notes: attrs.notes || undefined,
      sapPaymentDocument: attrs.sapPaymentDocument || undefined,
      createdAt: attrs.createdAt || new Date().toISOString(),
    }
  },

  /**
   * Trigger outbound SAP sync for a specific payment.
   */
  async syncPaymentOutbound(paymentId: string): Promise<{ sapPaymentDocument: string | null; sapSyncedAt: string | null }> {
    const http = getHttpClient()
    const response = await http.post<{ data: { sapPaymentDocument: string | null; sapSyncedAt: string | null } }>(`/api/payments/${paymentId}/sync-outbound`)
    return response.data.data
  },

  /**
   * Trigger on-demand SAP payment sync for current tenant.
   * Calls the custom sync endpoint built in payment-custom controller.
   */
  async syncPayments(): Promise<void> {
    const http = getHttpClient()
    await http.post('/api/payments/sync')
  },

  /**
   * Get Statement of Account for a date range.
   * Includes opening balance, closing balance, and all transactions in period.
   */
  async getStatement(periodStart: string, periodEnd: string): Promise<Statement> {
    const http = getHttpClient()
    const response = await http.get<StatementResponse>(
      `/api/transactions/statement?from=${periodStart}&to=${periodEnd}`
    )
    return response.data.data
  },

  /**
   * Get financial statistics for dashboard widgets.
   * Includes receivables, payables, pending payments, overdue count.
   */
  async getFinanceStats(): Promise<FinanceStats> {
    const http = getHttpClient()
    const response = await http.get<StatsResponse>('/api/transactions/stats')
    return response.data.data
  },
}

// ============================================================
// INVOICE API (SAP Inbound — read-only)
// ============================================================

interface InvoiceListResponse {
  data: any[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

interface InvoiceDetailResponse {
  data: any
}

function transformInvoiceResponse(item: any): Invoice {
  return {
    id: String(item.id),
    invoiceNumber: item.invoiceNumber || item.sapInvoiceNumber || '',
    status: item.status || 'issued',
    issueDate: item.issueDate || '',
    total: item.total || { amount: 0, currency: 'SAR' },
    netAmount: item.netAmount || { amount: 0, currency: 'SAR' },
    taxAmount: item.taxAmount || { amount: 0, currency: 'SAR' },
    currency: item.currency || 'SAR',
    sapInvoiceNumber: item.sapInvoiceNumber || '',
    sapCustomerCode: item.sapCustomerCode || '',
    documentReference: item.documentReference || undefined,
    billingDocumentType: item.billingDocumentType || undefined,
    salesOrganization: item.salesOrganization || undefined,
    origin: item.origin || 'sap',
    pdfUrl: item.pdfUrl || null,
    lines: item.lines?.map((line: any) => ({
      id: String(line.id),
      description: line.description || '',
      quantity: line.quantity || 0,
      quantityUnit: line.quantityUnit || '',
      unitPrice: line.unitPrice || 0,
      lineTotal: line.lineTotal || 0,
      material: line.material || '',
      materialGroup: line.materialGroup || '',
      plant: line.plant || '',
      sapBillingDocumentItem: line.sapBillingDocumentItem || '',
    })) || undefined,
    createdAt: item.createdAt || '',
  }
}

export const invoiceApi = {
  async syncInvoices(): Promise<void> {
    const http = getHttpClient()
    await http.post('/api/invoices/sync')
  },

  async getInvoicesByOrderNumbers(orderNumbers: string[]): Promise<Invoice[]> {
    if (orderNumbers.length === 0) return []
    const http = getHttpClient()
    const response = await http.get<{ data: any[] }>(`/api/invoices/by-orders?orderNumbers=${orderNumbers.join(',')}`)
    const rawData = Array.isArray(response.data.data) ? response.data.data : []
    return rawData.map(transformInvoiceResponse)
  },

  async getInvoices(
    filter?: InvoiceFilter,
    page = 1,
    pageSize = 20,
    sort = 'issueDate:desc'
  ): Promise<InvoicesResponse> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('pageSize', String(pageSize))
    params.append('sort', sort)

    if (filter?.dateRange?.from) {
      params.append('from', filter.dateRange.from)
    }
    if (filter?.dateRange?.to) {
      params.append('to', filter.dateRange.to)
    }
    if (filter?.search) {
      params.append('search', filter.search)
    }

    const response = await http.get<InvoiceListResponse>(`/api/invoices/list?${params.toString()}`)
    const rawData = Array.isArray(response.data.data) ? response.data.data : []

    return {
      invoices: rawData.map(transformInvoiceResponse),
      meta: response.data.meta?.pagination ?? { page: 1, pageSize: 20, pageCount: 0, total: 0 },
    }
  },

  async getInvoice(id: string): Promise<Invoice> {
    const http = getHttpClient()
    const response = await http.get<InvoiceDetailResponse>(`/api/invoices/${id}/detail`)
    return transformInvoiceResponse(response.data.data)
  },
}
